/**
 * Web dev logger.
 *
 * Goals:
 *   - On boot, print a coloured version banner with `__BUILD_VERSION__`
 *     and `__BUILD_TIME__` (defined by vite.config.ts). Helps Vincent
 *     confirm which deploy he's on when he opens DevTools.
 *   - In dev (`import.meta.env.DEV` or `MODE !== "production"`),
 *     surface auth state changes, API mutations, route changes, and
 *     query errors via `console.debug` / `console.info` with a
 *     consistent `[isale:xxx]` tag prefix so they're easy to filter.
 *   - In prod, everything below `warn` is silenced — Sentry takes over.
 *
 * Filtering tip (paste into DevTools console filter):
 *   [isale:auth]    — sign-in flow + session purge events
 *   [isale:api]     — request/response, mutations
 *   [isale:route]   — react-router navigation
 *   [isale:upload]  — MP14 dropzone state transitions
 *   [isale]         — anything iSale-tagged
 */

// Boot-banner CSS strings — built from design tokens so the source has no
// raw hex literals (design-drift hook stays clean).
import { color } from "./tokens";

const STYLE_BANNER = [
  `background:linear-gradient(135deg,${color.indigo[500]},${color.indigo[400]} 50%,${color.pink[400]})`,
  `color:${color.slate[50]}`,
  "padding:6px 12px",
  "border-radius:6px",
  "font-weight:800",
  "font-size:12px",
].join(";");

const STYLE_META = `color:${color.slate[500]};font-size:11px;font-family:monospace`;

// Verbose logging enabled when:
//   1. Local dev (Vite `pnpm dev` → MODE=development),
//   2. OR the bundle's baked API URL points at the `isale-service-dev`
//      cloud Worker — so deployed Pages on `*.pages.dev` still surfaces
//      the dev-tagged logs in DevTools (Vincent observing remote dev).
// Prod (`MODE=production` + api → `api.isale.app`) silences them.
const isDev =
  import.meta.env.DEV ||
  import.meta.env.MODE !== "production" ||
  (import.meta.env.VITE_API_URL ?? "").includes("isale-service-dev");

/** Print the boot banner. Called once from `main.tsx`. */
export function printBootBanner(): void {
  // Coloured pill with brand gradient; survives Safari/Chrome/Firefox.
  // eslint-disable-next-line no-console
  console.log(
    `%c iSale Web `,
    STYLE_BANNER,
    `\n  build:  ${__BUILD_VERSION__}\n  built:  ${__BUILD_TIME__}\n  mode:   ${import.meta.env.MODE}\n  api:    ${import.meta.env.VITE_API_URL ?? "<unset>"}\n`,
  );
  // Plain unstyled INFO line right after the banner — `%c` styles get
  // stripped or rendered awkwardly inside eruda's mobile console, so the
  // tagged plain line is what Vincent reads on phone. Easy to grep too.
  // eslint-disable-next-line no-console
  console.info(
    `[isale:version] iSale Web v${__BUILD_VERSION__} built ${__BUILD_TIME__} · mode=${import.meta.env.MODE} · api=${import.meta.env.VITE_API_URL ?? "<unset>"}`,
  );
  // Dim secondary line so it doesn't fight for attention with errors.
  // eslint-disable-next-line no-console
  console.log(
    "%c  → DevTools filter [isale:xxx] for tagged dev events · type `__ISALE__` to inspect build metadata",
    STYLE_META,
  );
  // Expose the build metadata + a tiny inspector to `window` so Vincent
  // can type `__ISALE__` in DevTools / eruda to see version, mode, API
  // URL, and a `since()` helper that prints elapsed time since boot.
  // Read-only on purpose (Object.freeze) so user code can't shadow it.
  if (typeof window !== "undefined") {
    const bootedAt = Date.now();
    (window as unknown as { __ISALE__?: unknown }).__ISALE__ = Object.freeze({
      version: __BUILD_VERSION__,
      built: __BUILD_TIME__,
      mode: import.meta.env.MODE,
      api: import.meta.env.VITE_API_URL ?? null,
      bootedAt: new Date(bootedAt).toISOString(),
      /** Seconds since the page booted — quick "how long has this tab been open" check. */
      uptimeSec: () => Math.round((Date.now() - bootedAt) / 1000),
    });
  }
}

/** Internal — emit a debug log only in dev. */
function devDebug(tag: string, ...args: unknown[]): void {
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.debug(`[${tag}]`, ...args);
}

/** Internal — emit an info log only in dev. */
function devInfo(tag: string, ...args: unknown[]): void {
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.info(`[${tag}]`, ...args);
}

export const log = {
  /** Dev-only events. Filtered via `[isale:auth]` in DevTools console. */
  auth: (msg: string, data?: Record<string, unknown>) =>
    devInfo("isale:auth", msg, data ?? ""),
  api: (msg: string, data?: Record<string, unknown>) =>
    devDebug("isale:api", msg, data ?? ""),
  route: (msg: string, data?: Record<string, unknown>) =>
    devDebug("isale:route", msg, data ?? ""),
  upload: (msg: string, data?: Record<string, unknown>) =>
    devDebug("isale:upload", msg, data ?? ""),
  /** Always-on warning. Use for unexpected-but-recoverable conditions. */
  warn: (tag: string, msg: string, data?: Record<string, unknown>) => {
    // eslint-disable-next-line no-console
    console.warn(`[isale:${tag}]`, msg, data ?? "");
  },
  /** Always-on error. In production Sentry catches uncaught throws too. */
  error: (tag: string, msg: string, err?: unknown) => {
    // eslint-disable-next-line no-console
    console.error(`[isale:${tag}]`, msg, err);
  },
};

/** Quick access to the build metadata for runtime (Settings footer, etc.). */
export const BUILD_INFO = {
  version: __BUILD_VERSION__,
  time: __BUILD_TIME__,
  mode: import.meta.env.MODE,
};
